import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  PlusOutlined,
  PrinterOutlined,
  SearchOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import { getApiErrorMessage } from "../../utils/apiError";
import { getEnteredByUser } from "../../utils/currentUser";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import type { DriverDto, LorryHireChallanDto, VehicleDto } from "../../types/api";
import { printLorryHireStationery } from "../../shared/printing/transportPrintTemplates";
import { useOperationsWorkspace } from "../../shared/components/OperationsTransactionTabs";

type PartyLookup = { id: number; label: string };

const STATUS_COLOR: Record<string, string> = {
  Draft: "default",
  Confirmed: "blue",
  Settled: "green",
  Cancelled: "red"
};

const PAYMENT_MODES = ["Cash", "NEFT", "RTGS", "Cheque", "UPI", "DD"];

export const LorryHirePage = () => {
  const workspace = useOperationsWorkspace();
  const [rows, setRows] = useState<LorryHireChallanDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [form] = Form.useForm();

  const [vehicles, setVehicles] = useState<VehicleDto[]>([]);
  const [drivers, setDrivers] = useState<DriverDto[]>([]);
  const [parties, setParties] = useState<PartyLookup[]>([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<LorryHireChallanDto | null>(null);
  const [settleForm] = Form.useForm();
  const [settleOpen, setSettleOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await transportApi.listLorryHireChallans({ page, pageSize, q: search.trim() || undefined, status: statusFilter });
      setRows(result.data ?? []);
      setTotal(result.total ?? 0);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to load lorry hire challans"));
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const [v, d, pl] = await Promise.all([
        transportApi.listVehicles({ page: 1, pageSize: 500 }),
        transportApi.listDrivers({ page: 1, pageSize: 500 }),
        transportApi.searchPartyLookups("", 1, 500)
      ]);
      setVehicles(v.data);
      setDrivers(d.data);
      setParties(pl.items.map((x) => ({ id: x.id, label: x.label })));
    } catch { /* non-critical */ }
  };

  useEffect(() => { if (workspace?.mode === "data") void load(); }, [page, pageSize, statusFilter, workspace?.mode]);
  useEffect(() => { void loadLookups(); }, []);
  useEffect(() => {
    if (workspace?.mode === "entry") {
      setEditingId(null); form.resetFields(); form.setFieldsValue({ challanDate: dayjs() }); setOpen(true);
    }
  }, [workspace?.mode]);

  const onSearch = () => { setPage(1); void load(); };

  const onCreateOrUpdate = async () => {
    const values = await form.validateFields();
    const ctx = getDefaultContext();
    const payload = {
      ...ctx,
      enteredBy: getEnteredByUser(),
      challanDate: values.challanDate?.toISOString() ?? new Date().toISOString(),
      vehicleId: values.vehicleId,
      driverId: values.driverId ?? null,
      ownerPartyId: values.ownerPartyId ?? null,
      brokerPartyId: values.brokerPartyId ?? null,
      fromStation: values.fromStation,
      actualFromStation: values.actualFromStation ?? null,
      toStation: values.toStation,
      actualToStation: values.actualToStation ?? null,
      packages: values.packages ?? 0,
      actualWeight: values.actualWeight ?? 0,
      freightAmount: values.freightAmount ?? 0,
      ratePerTon: values.ratePerTon ?? 0,
      minGuarantee: values.minGuarantee ?? 0,
      advancePaid: values.advancePaid ?? 0,
      tdsAmount: values.tdsAmount ?? 0,
      otherDeductions: values.otherDeductions ?? 0,
      otherAdditions: values.otherAdditions ?? 0,
      payableAtBranchId: values.payableAtBranchId ?? null,
      arrivalDate: values.arrivalDate ? values.arrivalDate.toISOString() : null,
      isTdsApplied: values.isTdsApplied ?? false,
      hasRC: values.hasRC ?? false,
      hasForm15I: values.hasForm15I ?? false,
      panNo: values.panNo ?? null,
      rePanNo: values.rePanNo ?? null,
      remarks: values.remarks ?? null,
    };

    try {
      setSaving(true);
      if (editingId) {
        await transportApi.updateLorryHireChallan(editingId, payload);
        message.success("Challan updated");
      } else {
        await transportApi.createLorryHireChallan(payload);
        message.success("Challan created");
      }
      setOpen(false);
      setEditingId(null);
      form.resetFields();
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to save challan"));
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row: LorryHireChallanDto) => {
    setEditingId(row.id);
    form.setFieldsValue({
      challanDate: dayjs(row.challanDate),
      vehicleId: row.vehicleId,
      driverId: row.driverId,
      ownerPartyId: row.ownerPartyId,
      brokerPartyId: row.brokerPartyId,
      fromStation: row.fromStation,
      actualFromStation: row.actualFromStation,
      toStation: row.toStation,
      actualToStation: row.actualToStation,
      packages: row.packages,
      actualWeight: row.actualWeight,
      freightAmount: row.freightAmount,
      ratePerTon: row.ratePerTon,
      minGuarantee: row.minGuarantee,
      advancePaid: row.advancePaid,
      tdsAmount: row.tdsAmount,
      otherDeductions: row.otherDeductions,
      otherAdditions: row.otherAdditions,
      arrivalDate: row.arrivalDate ? dayjs(row.arrivalDate) : undefined,
      isTdsApplied: row.isTdsApplied,
      hasRC: row.hasRC,
      hasForm15I: row.hasForm15I,
      panNo: row.panNo,
      rePanNo: row.rePanNo,
      remarks: row.remarks,
    });
    setOpen(true);
  };

  const onViewDetail = (row: LorryHireChallanDto) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const onPrint = async (row: LorryHireChallanDto) => {
    try {
      const report = await transportApi.getLorryHirePrintReport(row.id);
      printLorryHireStationery(report);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to print lorry challan"));
    }
  };

  const onConfirm = async (id: number) => {
    try {
      await transportApi.confirmLorryHireChallan(id);
      message.success("Challan confirmed");
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Could not confirm"));
    }
  };

  const onCancel = async (id: number) => {
    Modal.confirm({
      title: "Cancel Challan",
      content: "This action cannot be undone. Cancel this challan?",
      okText: "Yes, Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await transportApi.cancelLorryHireChallan(id, "Cancelled by user");
          message.success("Challan cancelled");
          await load();
        } catch (err) {
          message.error(getApiErrorMessage(err, "Could not cancel"));
        }
      }
    });
  };

  const onSettle = async () => {
    if (!detailRow) return;
    const values = await settleForm.validateFields();
    try {
      await transportApi.settleLorryHireChallan(detailRow.id, {
        paymentReference: values.paymentReference,
        paymentMode: values.paymentMode,
        settlementDate: values.settlementDate ? values.settlementDate.toISOString() : undefined
      });
      message.success("Challan settled successfully");
      setSettleOpen(false);
      setDetailOpen(false);
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Could not settle"));
    }
  };

  const vehicleOptions = useMemo(
    () => vehicles.map(v => ({ value: v.id, label: `${v.vehicleNo} (${v.vehicleType})` })),
    [vehicles]
  );
  const driverOptions = useMemo(
    () => drivers.map(d => ({ value: d.id, label: `${d.driverName} — ${d.mobile}` })),
    [drivers]
  );
  const partyOptions = useMemo(
    () => parties.map(p => ({ value: p.id, label: p.label })),
    [parties]
  );

  return (
    <Card className="panel-card">
      <div className="panel-title-row operations-data-only">
        <Typography.Title level={3}>Lorry Hire Challans</Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setEditingId(null); form.resetFields(); form.setFieldsValue({ challanDate: dayjs() }); setOpen(true); }}
        >
          New Challan
        </Button>
      </div>

      <Space className="operations-data-only" style={{ marginBottom: 16 }} wrap>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search challan no, vehicle, route..."
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={onSearch}
          style={{ width: 300 }}
        />
        <Select
          placeholder="All Statuses"
          allowClear
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={[
            { value: "Draft", label: "Draft" },
            { value: "Confirmed", label: "Confirmed" },
            { value: "Settled", label: "Settled" },
            { value: "Cancelled", label: "Cancelled" },
          ]}
          style={{ width: 160 }}
        />
        <Button onClick={onSearch}>Search</Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); }
        }}
        columns={[
          { title: "#", width: 60, render: (_, __, i) => ((page - 1) * pageSize) + i + 1 },
          { title: "Challan No", dataIndex: "challanNo", width: 130, render: (v, row) => <Button type="link" size="small" onClick={() => onViewDetail(row)}>{v}</Button> },
          { title: "Date", dataIndex: "challanDate", width: 100, render: (v: string) => dayjs(v).format("DD-MMM-YYYY") },
          { title: "Vehicle", render: (_, row) => row.vehicle?.vehicleNo ?? `V-${row.vehicleId}` },
          { title: "From", dataIndex: "fromStation" },
          { title: "To", dataIndex: "toStation" },
          { title: "Freight", dataIndex: "freightAmount", align: "right", width: 100, render: (v: number) => `₹${v.toLocaleString()}` },
          { title: "Advance", dataIndex: "advancePaid", align: "right", width: 90, render: (v: number) => `₹${v.toLocaleString()}` },
          { title: "Balance", dataIndex: "balancePayable", align: "right", width: 90, render: (v: number) => <Typography.Text type={v > 0 ? "danger" : "secondary"}>₹{v.toLocaleString()}</Typography.Text> },
          {
            title: "Status", dataIndex: "status", width: 100,
            render: (v: string) => <Tag color={STATUS_COLOR[v] ?? "default"}>{v}</Tag>
          },
          {
            title: "Actions", width: 150,
            render: (_, row) => (
              <Space size={4}>
                <Tooltip placement="bottom" title="Print"><Button size="small" icon={<PrinterOutlined />} onClick={() => void onPrint(row)} /></Tooltip>
                {row.status === "Draft" && (
                  <>
                    <Button size="small" onClick={() => onEdit(row)}>Edit</Button>
                    <Tooltip placement="bottom" title="Confirm"><Button size="small" icon={<CheckCircleOutlined />} onClick={() => void onConfirm(row.id)} /></Tooltip>
                    <Tooltip placement="bottom" title="Cancel"><Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => void onCancel(row.id)} /></Tooltip>
                  </>
                )}
                {row.status === "Confirmed" && (
                  <>
                    <Tooltip placement="bottom" title="Settle"><Button size="small" icon={<DollarOutlined />} onClick={() => { setDetailRow(row); setSettleOpen(true); }} /></Tooltip>
                    <Tooltip placement="bottom" title="Cancel"><Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => void onCancel(row.id)} /></Tooltip>
                  </>
                )}
              </Space>
            )
          }
        ]}
      />

      {/* Challan Entry Drawer */}
      <StaticDrawer
        title={editingId ? `Edit Challan` : "New Lorry Hire Challan"}
        open={open}
        onClose={() => setOpen(false)}
        width={860}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setOpen(false)}>Cancel</Button><SaveActionButton saving={saving} idleText={editingId ? "Update" : "Save"} savingText={editingId ? "Updating..." : "Saving..."} onClick={() => void onCreateOrUpdate()} /></div>}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Divider orientation="left" plain>Challan Details</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="challanDate" label="Challan Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="vehicleId" label="Vehicle" rules={[{ required: true }]}>
                <Select showSearch options={vehicleOptions} placeholder="Select vehicle..." filterOption={(input, opt) => (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="driverId" label="Driver">
                <Select showSearch options={driverOptions} placeholder="Select driver..." allowClear filterOption={(input, opt) => (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ownerPartyId" label="Owner">
                <Select showSearch options={partyOptions} placeholder="Owner party..." allowClear filterOption={(input, opt) => (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="brokerPartyId" label="Broker">
                <Select showSearch options={partyOptions} placeholder="Broker party..." allowClear filterOption={(input, opt) => (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Route</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fromStation" label="From Station" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="toStation" label="To Station" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="actualFromStation" label="Actual From Station">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="actualToStation" label="Actual To Station">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="packages" label="Packages">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="actualWeight" label="Actual Weight (T)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="arrivalDate" label="Arrival Date">
                <DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Freight & Financials</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="freightAmount" label="Freight Amount (₹)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="ratePerTon" label="Rate / Ton (₹)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="minGuarantee" label="Min. Guarantee (₹)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} prefix="₹" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="advancePaid" label="Advance Paid (₹)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="tdsAmount" label="TDS Amount (₹)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="otherDeductions" label="Other Deductions (₹)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="otherAdditions" label="Other Additions (₹)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} prefix="₹" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Compliance</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="panNo" label="PAN No">
                <Input maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="rePanNo" label="Re-PAN No">
                <Input maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="isTdsApplied" valuePropName="checked">
                <Checkbox>TDS Applied</Checkbox>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="hasRC" valuePropName="checked">
                <Checkbox>Has RC</Checkbox>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="hasForm15I" valuePropName="checked">
                <Checkbox>Has Form 15-I</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </StaticDrawer>

      {/* Detail Drawer */}
      <StaticDrawer
        title={`Challan: ${detailRow?.challanNo ?? ""}`}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={680}
      >
        {detailRow && (
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Status" span={2}>
              <Tag color={STATUS_COLOR[detailRow.status]}>{detailRow.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Date">{dayjs(detailRow.challanDate).format("DD-MMM-YYYY")}</Descriptions.Item>
            <Descriptions.Item label="Vehicle">{detailRow.vehicle?.vehicleNo ?? `V-${detailRow.vehicleId}`}</Descriptions.Item>
            <Descriptions.Item label="From">{detailRow.fromStation}</Descriptions.Item>
            <Descriptions.Item label="To">{detailRow.toStation}</Descriptions.Item>
            <Descriptions.Item label="Owner">{detailRow.ownerParty?.partyName ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Broker">{detailRow.brokerParty?.partyName ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Packages">{detailRow.packages}</Descriptions.Item>
            <Descriptions.Item label="Actual Weight">{detailRow.actualWeight} T</Descriptions.Item>
            <Descriptions.Item label="Freight">₹{detailRow.freightAmount.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Rate/Ton">₹{detailRow.ratePerTon.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Advance Paid">₹{detailRow.advancePaid.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="TDS">₹{detailRow.tdsAmount.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Other Deductions">₹{detailRow.otherDeductions.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Other Additions">₹{detailRow.otherAdditions.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Balance Payable" span={2}>
              <Typography.Text strong type="danger">₹{detailRow.balancePayable.toLocaleString()}</Typography.Text>
            </Descriptions.Item>
            {detailRow.paymentReference && (
              <>
                <Descriptions.Item label="Payment Ref">{detailRow.paymentReference}</Descriptions.Item>
                <Descriptions.Item label="Payment Mode">{detailRow.paymentMode}</Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="TDS Applied"><Badge status={detailRow.isTdsApplied ? "success" : "default"} text={detailRow.isTdsApplied ? "Yes" : "No"} /></Descriptions.Item>
            <Descriptions.Item label="Has RC"><Badge status={detailRow.hasRC ? "success" : "default"} text={detailRow.hasRC ? "Yes" : "No"} /></Descriptions.Item>
            <Descriptions.Item label="Has Form 15-I"><Badge status={detailRow.hasForm15I ? "success" : "default"} text={detailRow.hasForm15I ? "Yes" : "No"} /></Descriptions.Item>
            <Descriptions.Item label="PAN No">{detailRow.panNo ?? "—"}</Descriptions.Item>
            {detailRow.remarks && <Descriptions.Item label="Remarks" span={2}>{detailRow.remarks}</Descriptions.Item>}
          </Descriptions>
        )}

        {detailRow?.status === "Confirmed" && (
          <div style={{ marginTop: 16 }}>
            <Space>
              <Button type="primary" icon={<DollarOutlined />} onClick={() => setSettleOpen(true)}>
                Record Settlement
              </Button>
            </Space>
          </div>
        )}
        {detailRow && (
          <div style={{ marginTop: 16 }}>
            <Button icon={<PrinterOutlined />} onClick={() => void onPrint(detailRow)}>Print</Button>
          </div>
        )}
      </StaticDrawer>

      {/* Settle Modal */}
      <Modal
        title="Record Settlement"
        open={settleOpen}
        onCancel={() => setSettleOpen(false)}
        onOk={() => void onSettle()}
        okText="Settle"
      >
        <Form form={settleForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="paymentReference" label="Payment Reference">
            <Input />
          </Form.Item>
          <Form.Item name="paymentMode" label="Payment Mode">
            <Select options={PAYMENT_MODES.map(m => ({ value: m, label: m }))} />
          </Form.Item>
          <Form.Item name="settlementDate" label="Settlement Date">
            <DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
